import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { camelCase, debounce, flatten, isEmpty, noop, uniq } from 'lodash';

import { PromptExit } from 'libs/router';

import { Form, FormField } from 'components/form';
import InfoNote from 'components/info-note/InfoNote';
import Input from 'components/inputs/input/Input';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import ActionButton from 'components/buttons/action-button/ActionButton';
import Pagination from 'components/pagination/Pagination';
import Spinner from 'components/spinner/Spinner';
import { ModalFrame, ModalFrameFooter, ModalFrameInner, ModalFrameMessage } from 'components/modal';
import TabsUnderlined from 'components/tabs/tabs-underlined/TabsUnderlined';
import TaskSelector from 'components/tasks/task-selector/TaskSelector';

import BulkEditActionTypeForm from '../bulk-edit-action-type-form/BulkEditActionTypeForm';
import SubcampaignsDeliverablesRowsCustom from 'modules/subcampaigns/components/bulk/subcampaigns-deliverables-rows/SubcampaignsDeliverablesRowsCustom';

import {
  deselectAllDeliverables,
  fetchCampaignDeliverablesIdsByInfluencer,
  fetchDeliverablesByInfluencer,
  resetDeliverables,
  selectAllDeliverables,
  toggleCreatorSelection,
  toggleDeliverableByType,
  toggleDeliverableSelection,
  updateOffers
} from 'modules/campaigns/reducers/campaignDeliverablesByInfluencer';

import { addUserAction } from 'modules/tracking/providers/user-action-provider';
import { getChoseActionForBulkOfferUpdateEventLabel } from 'modules/tracking/utils/user-actions';
import { __t, applyTranslations } from 'utils/language';
import {
  DELIVERABLE_ADD_REPLACE_ZALANDO_PAGE_ACTION,
  DELIVERABLE_ADD_WMC_LINK_ACTION,
  DELIVERABLE_ADD_ZALANDO_PAGE_ACTION,
  DELIVERABLE_ICONS,
  DELIVERABLE_POSTPONE_ACTION,
  DELIVERABLE_REPLACE_TIMELINE_ACTION,
  DELIVERABLE_REPLACE_WMC_LINK_ACTION,
  DELIVERABLE_REPLACE_ZALANDO_PAGE_ACTION,
  isZalandoOutfitOnly
} from 'utils/deliverables';
import { isApplicableDeliverableType } from 'utils/bulk-offer-edit';
import { minLengthArray } from 'utils/validators';
import { NUM_DAYS_DEFAULT_POSTPONE, SUBCAMPAIGN_STATE_ACCEPTED, SUBCAMPAIGN_STATE_PENDING } from 'utils/subcampaign';

import type {
  CustomReportDeliverable,
  DeliverablePersistedWMCLink,
  DeliverablePreview,
  DeliverableTag,
  DeliverableType,
  LocationWindow,
  Pagination as PaginationType,
  SortableTableSort,
  TimeframeResult
} from 'types';

import type {
  BulkEditZalandoLinkWmcRequest,
  BulkOfferModalSelectedTimelines,
  BulkOfferModalSubcampaignCustomOffer,
  BulkOfferModalTimelineData
} from 'modules/subcampaigns/types/BulkOfferModal.type';
import type {
  TaskSelectorItem
} from 'components/tasks/task-selector/types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./bulk-edit-offer-modal.i18n.json'));

type SelectedDeliverablesType = {
  [x: number]: {
    [x: number]: DeliverablePreview
  }
};

type SelectedDeliverablesByTypeMap = {
  [x: number]: {
    [x: number]: number
  }
};

type OwnProps = {
  campaignId: number,
  location: LocationWindow,
  sortingOptions: SortableTableSort, // eslint-disable-line react/no-unused-prop-types
  defaultStates?: Array<string>,
  onBeforeModalClosed: () => void,
  onCloseModal: () => void
};

type StateProps = {
  isLoading: boolean,
  pagination: PaginationType,
  groupedDeliverables: Array<BulkOfferModalSubcampaignCustomOffer>,
  groupedDeliverablesIds: Record<string, Array<number>>,
  selectedDeliverables: SelectedDeliverablesType,
  contentApprovalV2: boolean,
  isMultiZLinkEnabled: boolean
};

type DispatchProps = {
  dispatchToggleCreatorSelection: (creatorId: number, isChecked: boolean) => Promise<void>,
  dispatchToggleDeliverablesByType: (deliverablesMap: SelectedDeliverablesByTypeMap) => Promise<void>,
  dispatchToggleDeliverableSelection: (creatorId: number, deliverableId: number | string) => Promise<void>,
  dispatchDeselectAllDeliverables: () => Promise<any>,
  dispatchSelectAllDeliverables: () => Promise<any>,
  dispatchFetchCampaignDeliverablesIdsByInfluencer: (b: number, a: any) => Promise<any>,
  dispatchUpdateOffers: (c: number, b: string, a: any) => Promise<any>,
  dispatchFetchDeliverablesByInfluencer: (b: number, a: any) => Promise<any>,
  dispatchResetDeliverables: () => Promise<any>
};

type Props = StateProps & OwnProps & DispatchProps;

type State = {
  actionType: string,
  days: number,
  hashtags: Array<string>,
  tags: Array<string>,
  mentions: Array<string>,
  timeline: TimeframeResult,
  deliverableTag: DeliverableTag | null,
  description: string,
  wmcRequest: BulkEditZalandoLinkWmcRequest,
  query: string,
  expandedIdsMap: {
    [x: number]: boolean
  },
  isExpanded: boolean,
  states: Array<string>,
  pickedTimelines: BulkOfferModalSelectedTimelines,
  contentTimelines: BulkOfferModalTimelineData
};

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'bulk_edit_offer_modal';

const STATES = [SUBCAMPAIGN_STATE_ACCEPTED, SUBCAMPAIGN_STATE_PENDING];
const DEBOUNCE_DELAY = 400;

const patchDeliverables = (deliverables, actionType) => (
  deliverables.map(deliverable => ({
    ...deliverable,
    selectable: isApplicableDeliverableType(deliverable.deliverableType, actionType)
  }))
);

export class BulkEditOfferModal extends React.Component<Props, State> {
  wmcOptions: Array<string> = [
    DELIVERABLE_ADD_REPLACE_ZALANDO_PAGE_ACTION,
    DELIVERABLE_ADD_WMC_LINK_ACTION,
    DELIVERABLE_ADD_ZALANDO_PAGE_ACTION,
    DELIVERABLE_REPLACE_WMC_LINK_ACTION,
    DELIVERABLE_REPLACE_ZALANDO_PAGE_ACTION
  ];

  debounceMethod = noop;

  constructor(props) {
    super(props);

    this.state = {
      actionType: DELIVERABLE_POSTPONE_ACTION,
      days: NUM_DAYS_DEFAULT_POSTPONE,
      hashtags: [],
      tags: [],
      mentions: [],
      timeline: {
        startDate: '',
        endDate: ''
      },
      wmcRequest: {
        primary: {
          url: '',
          name: '',
          description: ''
        },
        secondary: {
          url: '',
          name: '',
          description: ''
        }
      },
      deliverableTag: null,
      description: '',
      query: '',
      expandedIdsMap: {},
      isExpanded: false,
      states: this.props.defaultStates ?? [SUBCAMPAIGN_STATE_ACCEPTED, SUBCAMPAIGN_STATE_PENDING],
      pickedTimelines: this.getDefaultPickedTimelines(),
      contentTimelines: {
        contentApproval: {
          startDate: '',
          endDate: ''
        },
        goLive: {
          startDate: '',
          endDate: ''
        },
        insightsProvision: {
          startDate: '',
          endDate: ''
        }
      }
    };

    this.debounceMethod = debounce(this.handleQueryFetch, DEBOUNCE_DELAY);
  }

  componentDidMount() {
    const {
      campaignId,
      dispatchFetchDeliverablesByInfluencer,
      dispatchFetchCampaignDeliverablesIdsByInfluencer
    } = this.props;
    const { states } = this.state;

    dispatchFetchCampaignDeliverablesIdsByInfluencer(campaignId, { states });
    dispatchFetchDeliverablesByInfluencer(campaignId, { states });
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.groupedDeliverables !== prevProps.groupedDeliverables) {
      this.setState(state => ({ // eslint-disable-line react/no-did-update-set-state
        expandedIdsMap: {
          ...this.getExpandedIdsMap(state.isExpanded),
          ...state.expandedIdsMap
        }
      }));
    }
  }

  componentWillUnmount() {
    const { dispatchResetDeliverables } = this.props;
    dispatchResetDeliverables();
  }

  private getDefaultPickedTimelines = (): BulkOfferModalSelectedTimelines => ({
    contentApproval: false,
    goLive: false,
    insightsProvision: false
  });

  private getDeliverables = (): Array<CustomReportDeliverable> => {
    const { groupedDeliverables } = this.props;

    return groupedDeliverables.reduce((acc, item) => (
      acc.concat(item.deliverables)
    ), [] as Array<CustomReportDeliverable>);
  };

  private getIsZalandoOutfitOnlyStatus = (): boolean => (
    isZalandoOutfitOnly(this.getDeliverables())
  );

  private getExpandedIdsMap = (isExpanded: boolean) => this.props.groupedDeliverables.reduce((res, item) => {
    res[item.creatorProfile.id] = isExpanded;
    return res;
  }, {});

  private getUpdatedActionType = (): string => {
    const { actionType } = this.state;

    if (
      actionType === DELIVERABLE_ADD_REPLACE_ZALANDO_PAGE_ACTION
      || actionType === DELIVERABLE_REPLACE_ZALANDO_PAGE_ACTION
    ) {
      return DELIVERABLE_REPLACE_WMC_LINK_ACTION;
    }

    if (actionType === DELIVERABLE_ADD_ZALANDO_PAGE_ACTION) {
      return DELIVERABLE_ADD_WMC_LINK_ACTION;
    }

    return actionType;
  };

  private prepareWmcRequest = (): DeliverablePersistedWMCLink => {
    const { actionType, wmcRequest } = this.state;

    if (
      actionType === DELIVERABLE_ADD_REPLACE_ZALANDO_PAGE_ACTION
      || actionType === DELIVERABLE_ADD_ZALANDO_PAGE_ACTION
    ) {
      return {
        baseUrl: wmcRequest.primary.url,
        name: wmcRequest.primary.name,
        description: wmcRequest.primary.description,
        country: '',
        language: ''
      };
    }

    return {
      baseUrl: wmcRequest.primary.url,
      newUrl: wmcRequest.secondary.url,
      name: wmcRequest.secondary.name,
      description: wmcRequest.secondary.description,
      country: '',
      language: ''
    };
  };

  private handleSubmit = () => {
    const {
      campaignId,
      dispatchUpdateOffers,
      onBeforeModalClosed,
      onCloseModal
    } = this.props;
    const {
      days,
      hashtags,
      actionType,
      tags,
      mentions,
      timeline,
      deliverableTag,
      description,
      contentTimelines,
      pickedTimelines
    } = this.state;

    const updatedActionType = this.getUpdatedActionType();

    dispatchUpdateOffers(campaignId, updatedActionType, {
      days: Number(days),
      hashtags,
      tags,
      mentions,
      timeline,
      deliverableTag,
      description,
      wmcRequest: this.prepareWmcRequest(),
      timelinesV2: {
        contentApproval: pickedTimelines.contentApproval,
        contentGoLiveDate: pickedTimelines.goLive,
        insightsProvision: pickedTimelines.insightsProvision,
        contentApprovalTimeline: contentTimelines.contentApproval,
        contentGoLiveDateTimeline: contentTimelines.goLive,
        insightsProvisionTimeline: contentTimelines.insightsProvision
      }
    })
      .then(() => {
        onBeforeModalClosed();
        onCloseModal();
        addUserAction({
          eventCategory: 'bulk update offer details',
          eventAction: 'choose an action',
          eventLabel: getChoseActionForBulkOfferUpdateEventLabel(actionType)
        });
      })
      .catch(() => {});
  };

  private handleEditActionTypeChange = (actionType: string) => {
    const defaultPickedTimelines = this.getDefaultPickedTimelines();
    const shouldResetPickedTimelines = actionType !== DELIVERABLE_POSTPONE_ACTION
      && actionType !== DELIVERABLE_REPLACE_TIMELINE_ACTION;

    this.setState(state => ({
      actionType,
      pickedTimelines: shouldResetPickedTimelines ? defaultPickedTimelines : state.pickedTimelines
    }));
  };

  private handleActionDataChange = (
    {
      name,
      value
    }: {
      name: string,
      value: string | Array<string> | TimeframeResult | DeliverableTag | DeliverablePersistedWMCLink | null
    }
  ) => {
    this.setState<never>({
      [name]: value
    });
  };

  private handleWmcChange = (
    {
      name,
      value
    }: {
      name: 'wmcRequest',
      value: BulkEditZalandoLinkWmcRequest
    }
  ): void => {
    this.setState<never>({
      [name]: value
    });
  };

  private handleToggleExpanded = (event: React.SyntheticEvent<any>) => {
    const target = event.currentTarget;
    const creatorId = target.dataset.key;

    this.setState(state => ({
      expandedIdsMap: {
        ...state.expandedIdsMap,
        [creatorId]: !state.expandedIdsMap[creatorId]
      }
    }));
  };

  private handleToggleAllExpanded = (isExpanded: boolean) => {
    this.setState({
      isExpanded,
      expandedIdsMap: this.getExpandedIdsMap(isExpanded)
    });
  };

  private handleQueryChanged = async (event: React.SyntheticEvent<HTMLSelectElement>) => {
    const target = event.currentTarget;
    const search = target.value;

    this.setState({
      query: search
    }, () => {
      if (this.debounceMethod) {
        this.debounceMethod(search);
      }
    });
  };

  private handleQueryFetch = (search: string): void => {
    const {
      campaignId,
      dispatchFetchDeliverablesByInfluencer,
      dispatchFetchCampaignDeliverablesIdsByInfluencer
    } = this.props;
    const { states } = this.state;

    dispatchFetchCampaignDeliverablesIdsByInfluencer(campaignId, { search, states });

    dispatchFetchDeliverablesByInfluencer(campaignId, {
      pageId: 1,
      search,
      states
    });
  };

  private handleStatesChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const {
      campaignId,
      dispatchFetchDeliverablesByInfluencer,
      dispatchFetchCampaignDeliverablesIdsByInfluencer
    } = this.props;
    const { states, query: search } = this.state;

    const target = event.currentTarget;
    const value = target.value;
    let newStates;

    if (target.checked) {
      newStates = ([...states, value]);
    } else {
      newStates = states.filter(s => s !== value);
    }

    this.setState({
      states: newStates
    });

    dispatchFetchCampaignDeliverablesIdsByInfluencer(campaignId, { search, states: newStates });

    dispatchFetchDeliverablesByInfluencer(campaignId, {
      pageId: 1,
      search,
      states: newStates
    });
  };

  private handleSelection = (isSelectAll: boolean) => {
    const { dispatchDeselectAllDeliverables, dispatchSelectAllDeliverables } = this.props;

    if (isSelectAll) {
      dispatchSelectAllDeliverables();
    } else {
      dispatchDeselectAllDeliverables();
    }
  };

  private handlePageChange = (pageId: number) => {
    const { campaignId, dispatchFetchDeliverablesByInfluencer } = this.props;
    const { states } = this.state;

    dispatchFetchDeliverablesByInfluencer(campaignId, {
      pageId,
      states,
      search: this.state.query
    });
  };

  private handleCreatorToggle = (creatorId: number, isChecked: boolean) => {
    const { dispatchToggleCreatorSelection } = this.props;
    dispatchToggleCreatorSelection(creatorId, isChecked);
  };

  private handleDeliverableToggle = (creatorId: number, deliverableId: string | number) => {
    const { dispatchToggleDeliverableSelection } = this.props;
    dispatchToggleDeliverableSelection(creatorId, Number(deliverableId));
  };

  private handleTaskTypeSelect = (
    items: Array<TaskSelectorItem>,
    identifier: string
  ) => {
    const { dispatchToggleDeliverablesByType } = this.props;
    const selectedDeliverableIds = this.getSelectedDeliverableIds();
    const visibleGroupedDeliverables = this.getFilteredData();
    const selectedDeliverableTypes = items.filter(item => item.isSelected)
      .map(item => item.identifier);

    const deliverablesMap = visibleGroupedDeliverables.reduce((result, group) => {
      const statusMap = group.deliverables
        .map(deliverable => ({
          [deliverable.id]: deliverable.deliverableType === identifier || identifier === 'select-all'
            ? selectedDeliverableTypes.includes(deliverable.deliverableType)
            : selectedDeliverableIds.includes(deliverable.id)
        }));

      return {
        ...result,
        [group.creatorProfile.id]: statusMap
      };
    }, {});

    dispatchToggleDeliverablesByType(deliverablesMap);
  };

  private getFilteredData = (): Array<BulkOfferModalSubcampaignCustomOffer> => {
    const { groupedDeliverables } = this.props;
    const { actionType } = this.state;

    return groupedDeliverables.map<BulkOfferModalSubcampaignCustomOffer>(group => ({
      ...group,
      deliverables: patchDeliverables(group.deliverables, actionType)
    }));
  };

  private getTooltipForDisabledDeliverable = (): string => {
    const tooltipPartStart = __t(`${MSG_KEY}.tooltips.parts.${this.state.actionType}`);
    const tooltipPartEnd = __t(`${MSG_KEY}.tooltips.parts.not_applicable`);

    return `${tooltipPartStart} ${tooltipPartEnd}`;
  };

  private isAllHasSelectedFlag(statusToCheck: boolean): boolean {
    const { groupedDeliverablesIds, selectedDeliverables } = this.props;

    if (isEmpty(groupedDeliverablesIds)) {
      return false;
    }

    return Object.keys(groupedDeliverablesIds)
      .flatMap((creatorId) =>
        groupedDeliverablesIds[creatorId]
          .every((id) => selectedDeliverables[creatorId]?.[id]?.isChecked === statusToCheck)
      )
      .every(Boolean);
  }

  private isAllHasExpandedFlag(statusToCheck: boolean): boolean {
    const { groupedDeliverablesIds } = this.props;
    const { expandedIdsMap } = this.state;

    if (isEmpty(groupedDeliverablesIds)) {
      return false;
    }

    return Object.keys(groupedDeliverablesIds).every((key) => expandedIdsMap[key] === statusToCheck);
  }

  private getSelectedDeliverableIds(): Array<number> {
    const { selectedDeliverables } = this.props;

    const deliverableIds = Object.values(selectedDeliverables)
      .map(deliverableMap =>
        Object.values(deliverableMap)
          .filter(deliverable => deliverable.isChecked)
          .map(deliverable => deliverable.id)
      );

    return flatten(deliverableIds);
  }

  private getSelectableDeliverableIds(): Array<number> {
    const visibleDeliverables = this.getFilteredData()
      .map(groupedDeliverable => (
        groupedDeliverable.deliverables
          .filter(deliverable => deliverable.selectable)
          .map(deliverable => deliverable)
      ));

    return flatten(visibleDeliverables)
      .map(deliverable => deliverable.id);
  }

  private getVisibleSelectedDeliverables(): Array<DeliverablePreview> {
    const { selectedDeliverables } = this.props;
    const selectableDeliverableIds = this.getSelectableDeliverableIds();

    const deliverables = Object.values(selectedDeliverables)
      .map(deliverableMap => (
        Object.values(deliverableMap)
          .filter(deliverable => selectableDeliverableIds.includes(deliverable.id))
          .filter(deliverable => deliverable.isChecked)
          .map(deliverable => deliverable)
      ));

    return uniq(flatten(deliverables));
  }

  private getVisibleSelectedTypes(): Array<DeliverableType> {
    const visibleDeliverables = this.getVisibleSelectedDeliverables();

    const deliverableTypes = visibleDeliverables
      .map(deliverable => deliverable.type);

    return uniq(flatten(deliverableTypes));
  }

  private getTaskTypeItems(): Array<TaskSelectorItem> {
    const { actionType } = this.state;
    const allowedTypes = this.getAllowedTypes();
    const selectedTypes = this.getVisibleSelectedTypes();

    return allowedTypes.map(type => {
      const selectable = isApplicableDeliverableType(type, actionType);

      return {
        identifier: type,
        label: __t(`${COMMON_MSG_KEY}.types.${type}.singular`),
        icon: DELIVERABLE_ICONS[camelCase(type)],
        isSelected: selectedTypes.includes(type) && selectable,
        selectable
      };
    });
  }

  private getAllowedTypes(): Array<DeliverableType> {
    const deliverableTypes = this.getFilteredData()
      .reduce<Array<DeliverableType>>((result, item) => {
        const types = item.deliverables.map(deliverable => deliverable.deliverableType);
        return result.concat(types);
      }, []);

    return uniq(deliverableTypes);
  }

  private renderSelect() {
    const { campaignId, contentApprovalV2, pagination, isLoading, groupedDeliverables, location } = this.props;
    const { expandedIdsMap } = this.state;

    if (isLoading) {
      return (
        <div className="subcampaigns-deliverables-select__spinner">
          <Spinner
            position="inline"
            size="small"
            color="light"
          />
        </div>
      );
    }

    if (!groupedDeliverables) {
      return null;
    }

    const expandedIds = Object.keys(expandedIdsMap).filter(id => expandedIdsMap[id]).map(id => Number(id));
    const checkedIds = this.getSelectedDeliverableIds();

    return (
      <React.Fragment>
        <SubcampaignsDeliverablesRowsCustom
          contentApprovalV2={contentApprovalV2}
          itemClassName="bulk-edit-offer-modal__creator-item-wrapper"
          allGroupedDeliverables={groupedDeliverables}
          visibleGroupedDeliverables={this.getFilteredData()}
          campaignId={campaignId}
          expandedIds={expandedIds}
          isZalandoOutfitOnly={this.getIsZalandoOutfitOnlyStatus()}
          onToggleExpanded={this.handleToggleExpanded}
          checkedDeliverables={checkedIds}
          onToggleCreatorSelection={this.handleCreatorToggle}
          onToggleDeliverableSelection={this.handleDeliverableToggle}
          getTooltipForDisabledDeliverable={this.getTooltipForDisabledDeliverable}
          includeRatio
        />
        <Pagination
          location={location}
          meta={pagination}
          links={pagination.links}
          onClick={this.handlePageChange}
          scrollToTopOnPageChange={false}
        />
      </React.Fragment>
    );
  }

  private renderFilterActions() {
    return (
      <div>
        <div className="bulk-edit-offer-modal__filter-action">
          <TabsUnderlined
            items={[
              {
                isActive: this.isAllHasSelectedFlag(true),
                handleClickArg: 'select',
                handleClick: () => this.handleSelection(true),
                label: __t(`${MSG_KEY}.filter_action.select_all`)
              }, {
                isActive: this.isAllHasSelectedFlag(false),
                handleClickArg: 'deselect',
                handleClick: () => this.handleSelection(false),
                label: __t(`${MSG_KEY}.filter_action.deselect_all`)
              }
            ]}
            size="normal"
          />
        </div>

        <div className="bulk-edit-offer-modal__filter-action">
          <TabsUnderlined
            items={[
              {
                isActive: this.isAllHasExpandedFlag(true),
                handleClickArg: 'expand',
                handleClick: () => this.handleToggleAllExpanded(true),
                label: __t(`${MSG_KEY}.filter_action.expand`)
              }, {
                isActive: this.isAllHasExpandedFlag(false),
                handleClickArg: 'collapse',
                handleClick: () => this.handleToggleAllExpanded(false),
                label: __t(`${MSG_KEY}.filter_action.collapse`)
              }
            ]}
            size="normal"
          />
        </div>
      </div>
    );
  }

  private renderStatesFilter() {
    const { states } = this.state;

    return (
      <div className="bulk-edit-offer-modal__statuses">
        <div className="bulk-edit-offer-modal__status-title">
          {__t(`${MSG_KEY}.state_filter_title`)}
        </div>
        <div className="bulk-edit-offer-modal__states">
          {
            STATES.map(state => (
              <div
                key={`bulk-edit-offer-modal-states-${state}`}
                className="bulk-edit-offer-modal__state"
              >
                <InputCheckbox
                  id={`bulk-edit-offer-modal-states-${state}`}
                  name="bulk-edit-offer-modal-states"
                  label={__t(`bulk_edit_offer_modal.state_filter_${state}`)}
                  checked={states.includes(state)}
                  value={state}
                  onChange={this.handleStatesChange}
                />
              </div>
            ))
          }
        </div>
      </div>
    );
  }

  private renderChangesByTaskType(): React.ReactNode {
    const items = this.getTaskTypeItems();
    const selectedTypes = this.getVisibleSelectedTypes();
    const tooltipMsg = this.getTooltipForDisabledDeliverable();

    return (
      <FormField
        showValidationIcon={false}
        direction="row"
        fontSize="small"
        className="bulk-edit-offer-modal__by-task-type"
        validators={[
          minLengthArray(selectedTypes, 1)
        ]}
      >
        <div className="bulk-edit-offer-modal__by-task-type-title">
          {__t(`${MSG_KEY}.tasks.title`)}
        </div>
        <TaskSelector
          items={items}
          selectedTypes={selectedTypes}
          tooltipMsg={tooltipMsg}
          withIcon
          withSelectAll
          onChange={this.handleTaskTypeSelect}
        />
      </FormField>
    );
  }

  private renderSearchFilter() {
    const { query } = this.state;

    return (
      <div className="bulk-edit-offer-modal__search">
        <div className="bulk-edit-offer-modal__search-label">
          {__t(`${MSG_KEY}.label_search`)}
        </div>
        <Input
          name="query"
          autoComplete="off"
          placeholder={__t(`${MSG_KEY}.placeholder_search`)}
          value={query}
          onChange={this.handleQueryChanged}
        />
      </div>
    );
  }

  private renderSelectedTaskCount() {
    const visibleSelectedDeliverables = this.getVisibleSelectedDeliverables();
    const visibleSelectedDeliverableIds = visibleSelectedDeliverables
      .map(deliverable => deliverable.id);

    const numSelectedItems = visibleSelectedDeliverableIds.length;
    const noTasksSelected = numSelectedItems < 1;

    if (noTasksSelected) {
      return null;
    }

    const containerClasses = classnames('bulk-edit-offer-modal__task-count', {
      'bulk-edit-offer-modal__task-count--hide': noTasksSelected
    });

    return (
      <div className={containerClasses}>
        <span className="bulk-edit-offer-modal__task-count--is-value">
          {numSelectedItems}
        </span>
        &nbsp;{__t(`${MSG_KEY}.tasks`, {
          withPlural: numSelectedItems
        })}
        &nbsp;{__t(`${MSG_KEY}.tasks.selected`)}
      </div>
    );
  }

  render() {
    const { contentApprovalV2, isMultiZLinkEnabled, groupedDeliverables, onCloseModal } = this.props;
    const {
      actionType,
      days,
      deliverableTag,
      description,
      hashtags,
      mentions,
      tags,
      timeline,
      pickedTimelines,
      contentTimelines
    } = this.state;

    return (
      <ModalFrame
        title={__t(`${MSG_KEY}.title`)}
      >
        <PromptExit type="modal" />
        <div className="bulk-edit-offer-modal__info">
          <InfoNote type="info">
            {__t(`${MSG_KEY}.change_info`)}
          </InfoNote>
        </div>
        <Form onSubmit={this.handleSubmit}>
          <ModalFrameInner>
            <BulkEditActionTypeForm
              groupedDeliverables={groupedDeliverables}
              selectedDeliverableIds={this.getSelectedDeliverableIds()}
              actionType={actionType}
              days={days}
              hashtags={hashtags}
              tags={tags}
              mentions={mentions}
              timeline={timeline}
              description={description}
              deliverableTag={deliverableTag}
              contentApprovalV2={contentApprovalV2}
              pickedTimelines={pickedTimelines}
              contentTimelines={contentTimelines}
              isZalandoOutfitOnly={this.getIsZalandoOutfitOnlyStatus()}
              isMultiZLinkEnabled={isMultiZLinkEnabled}
              handleTypeChange={this.handleEditActionTypeChange}
              onChange={this.handleActionDataChange}
              onWmcChange={this.handleWmcChange}
            />
            <div className="bulk-edit-offer-modal__title">
              {__t(`${MSG_KEY}.filters_title`)}
              {
                this.wmcOptions.includes(actionType) && (
                  <div className="bulk-edit-offer-modal__note-message">
                    {__t(`${MSG_KEY}.wmc_info_note_v2`)}
                  </div>
                )
              }
            </div>
            {this.renderChangesByTaskType()}
            {this.renderSearchFilter()}
            <div className="bulk-edit-offer-modal__filters">
              <div className="bulk-edit-offer-modal__filter-set">
                {this.renderSelectedTaskCount()}
                {this.renderFilterActions()}
              </div>
              {this.renderStatesFilter()}
            </div>
            {this.renderSelect()}
          </ModalFrameInner>
          <ModalFrameFooter>
            <ActionButton
              color="tertiary"
              onClick={onCloseModal}
            >
              {__t(`${COMMON_MSG_KEY}.action_button.cancel`)}
            </ActionButton>
            <ActionButton
              color="primary"
              type="submit"
            >
              {__t(`${MSG_KEY}.save_changes_buttton`)}
            </ActionButton>
            <ModalFrameMessage
              message={__t(`${MSG_KEY}.info`)}
            />
          </ModalFrameFooter>
        </Form>
      </ModalFrame>
    );
  }
}

const mapStateToProps = ({ campaignDeliverablesByInfluencer, campaignOverview, runtimeConfig }): StateProps => ({
  isLoading: campaignDeliverablesByInfluencer.isLoading,
  groupedDeliverables: campaignDeliverablesByInfluencer.groupedDeliverables,
  groupedDeliverablesIds: campaignDeliverablesByInfluencer.groupedDeliverablesIds,
  pagination: campaignDeliverablesByInfluencer.pagination,
  selectedDeliverables: campaignDeliverablesByInfluencer.selectedDeliverables,
  contentApprovalV2: campaignOverview.overview?.contentApprovalV2,
  isMultiZLinkEnabled: runtimeConfig.config.isMultiZLinkEnabled
});

const mapDispatchToProps = (dispatch, ownProps: OwnProps): DispatchProps => ({
  dispatchToggleCreatorSelection: (creatorId, isChecked) => dispatch(toggleCreatorSelection(creatorId, isChecked)),
  dispatchToggleDeliverableSelection: (creatorId, deliverableId) => dispatch(toggleDeliverableSelection(creatorId, deliverableId)),
  dispatchToggleDeliverablesByType: (deliverablesMap) => (
    dispatch(toggleDeliverableByType(deliverablesMap))
  ),

  dispatchFetchCampaignDeliverablesIdsByInfluencer: (campaignId, options) =>
    dispatch(fetchCampaignDeliverablesIdsByInfluencer(campaignId, options)),

  dispatchFetchDeliverablesByInfluencer: (campaignId, options) =>
    dispatch(fetchDeliverablesByInfluencer(campaignId, { ...options, ...(ownProps.sortingOptions || {}) })),

  dispatchResetDeliverables: () => dispatch(resetDeliverables()),
  dispatchUpdateOffers: (campaignId, action, options) => dispatch(updateOffers(campaignId, action, options)),
  dispatchDeselectAllDeliverables: () => dispatch(deselectAllDeliverables()),
  dispatchSelectAllDeliverables: () => dispatch(selectAllDeliverables())
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkEditOfferModal);
